import _ from 'lodash';
import moment from 'moment';
import * as ActionTypes from '../actions/actionTypes';
import graphsConfig from '../config/graphs';
import indicatorConfig from '../config/indicators';
import {DATA_SETS} from '../Constants';
import {convertedUnits, convertValue, emptyGraphMap, extractTicks, newDataPoint} from '../utils/point_utils';
import {addDays, formatForGraph, lastMidnightDate, roundedHour, utcToLocal} from '../utils/time_utils';
import {convertKtsToKph} from '../utils/wind_utils';

const indicatorConfigPropertyMap = _.groupBy(indicatorConfig, 'property_name');
const indicatorConfigAwsPropertyMap = _.keyBy(indicatorConfig, 'aws_property_name');

const _emptyGraphCache = () => {
  const map = {};

  graphsConfig.forEach(graph => {
    map[graph.id] = {};
  });

  return map;
};

const _createTableMetaData = () => {
  return _.sortBy(indicatorConfig, ['table_order_override', 'group', 'displayName'])
          .filter(i => i.point_data)
          .map(i => {
            return {
              key: i.prefix,
              only_in_app_mode: i.only_in_app_mode,
              displayName: (i.tableDisplayName || i.displayName) + convertedUnits(i),
              editable: !!i.editable,
              column_editable: !!i.column_editable,
              hide_in_table: !!i.hide_in_table,
              use_import_time_over_creation_time: !!i.use_import_time_over_creation_time,
              exclude_from_age_checks: !!i.exclude_from_age_checks,
              max: i.max,
              min: i.min,
              decPlaces: i.rounding_rule,
              colors: i.colors,
              dataSet: DATA_SETS.WEATHER,
              headerColor: '#67a1ea',
              arrow: i.arrow
            };
          });
};

// Takes the table cache and fills missing values.
const _fillTableAndGraphCache = (table_cache, graph_cache) => {
  const keys = indicatorConfig.filter(i => i.point_data_fillable).map(i => i.prefix);
  const list = _.sortBy(_.values(table_cache), 'sortIndex');

  // Store the relevant graph for each indicator.
  const relevantGraphs = {};
  keys.forEach(k => {
    graphsConfig.forEach(gc => {
      if (gc.series.filter(s => s.dataKey === k).length > 0) {
        relevantGraphs[k] = gc.id;
      }
    });
  });

  // Get the initial fill values so that we can fill from the start
  const fillValues = {};
  keys.forEach(k => {
    const data = _.find(list, e => (typeof e[k] !== 'undefined'));
    if (data) {
      fillValues[k] = data[k];
    }
  });

  list.forEach(e => {
    keys.forEach(k => {
      if (typeof e[k] === 'undefined') {
        // Fill the table data
        e[k] = fillValues[k];

        // Fill the graph data
        if (relevantGraphs[k]) {
          const graph_points = graph_cache[relevantGraphs[k]];
          const new_point = graph_points[e.name] || newDataPoint(e.name, e.time);
          if (typeof new_point[k] === 'undefined') {
            new_point[k] = fillValues[k];
          }
        }
      } else {
        fillValues[k] = e[k];
      }
    });
  });
};

// data_idx is incremented with each new request. Prevents the request overlapping.
const initialState = {
  data_idx: 0,
  graph_data: emptyGraphMap(graphsConfig),
  table_data: {},
  table_meta_data: _createTableMetaData(),
  graph_meta_data: graphsConfig,
  ticks: []
};

// This cache is used to load all of the graph data before adding it to the state.
let graph_cache = _emptyGraphCache();
let table_cache = {};

const reducer = (state = initialState, action) => {

  switch (action.type) {
    case ActionTypes.CLEAR_GRAPH_DATA:
      graph_cache = _emptyGraphCache();
      table_cache = {};
      return {
        ...initialState,
        data_idx: action.data_idx
      };

    case ActionTypes.POPULATE_INDICATOR_GRAPH_POINT:
      if (action.data.features && state.data_idx === action.data_idx) {
        const point_key = formatForGraph(action.time);
        const properties_array = action.data.features.map(f => f.properties);
        // We have a problem with AFDRS point data calls where all layers have the same property name: "GREY_INDEX"
        // To get around this, we first ensure that we only call one indicator at a time, and replace the
        // "GREY_INDEX" with the prefix.
        const values_array = _.map(properties_array, (p, idx) => {
          const greyValue = p['GRAY_INDEX'];
          if (greyValue === undefined) {
            return p;
          }
          const newKey = action.prefixes[idx];
          return _.set({}, newKey, greyValue);
        });
        const property_value_map = _.reduce(values_array, function (type, value) {
          return _.assign(type, value);
        }, {});
        const datakey_value_map = {};
        _.forOwn(property_value_map, (value, key) => {
          const configs = indicatorConfigPropertyMap[key];
          if (!configs) {
            console.log('ERROR: Failed to find config for property_name = ' + key);
          } else {
            configs.forEach((config) => datakey_value_map[config.prefix] = value);
          }
        });

        graphsConfig.forEach(graph => {
          const graph_points = graph_cache[graph.id];
          const new_point = graph_points[point_key] || newDataPoint(point_key, action.time);
          graph.series.forEach(series => {
            if (series.source === 'geoserver') {
              const key = series.dotValueKey || series.dataKey;
              const value = datakey_value_map[key];
              if (typeof value !== 'undefined') {
                new_point[key] = convertValue(key, value);
              }
            }
          });
          graph_points[point_key] = new_point;
        });
        if (values_array.length > 0) {
          _createTableMetaData().forEach(ind => {
            const key = ind.key;
            const new_point = table_cache[point_key] || newDataPoint(point_key, action.time);
            const value = datakey_value_map[key];
            if (typeof value !== 'undefined') {
              new_point[key] = convertValue(key, value);
            }
            table_cache[point_key] = new_point;
          });
        }
      }

      return state;

    case ActionTypes.POPULATE_AWS_GRAPH_DATA:
      if (state.data_idx === action.data_idx) {
        action.data.forEach((aws) => {
          const time = utcToLocal(moment(aws.obsdatetime).toDate());
          const roundedTime = roundedHour(time);
          if (roundedTime !== undefined) {
            const point_key = formatForGraph(roundedTime);
            graphsConfig.forEach(graph => {
              const graph_points = graph_cache[graph.id];
              const new_point = graph_points[point_key] || newDataPoint(point_key, time);
              graph.series.forEach(series => {
                if (series.source === 'aws') {
                  const key = series.dataKey;
                  let value = aws[key];
                  // Can't use !value since it might be 0.0
                  if (typeof value !== 'undefined' && value !== null) {
                    const layer = indicatorConfigAwsPropertyMap[key];
                    if (!layer) {
                      throw new Error('Could not find Indicator with aws_property_name of \'' + key + '\'.');
                    }
                    if (series.convertKts) {
                      value = convertKtsToKph(value);
                    }
                    value = Number(value.toFixed(layer.rounding_rule || 0));
                    new_point[key] = value;
                  }
                }
              });
              graph_points[point_key] = new_point;
            });
          }
        });
      }
      return state;

    case ActionTypes.GRAPH_DATA_LOADED:
      _fillTableAndGraphCache(table_cache, graph_cache);
    // Continue down

    // eslint-disable-next-line
    case ActionTypes.REBUILD_GRAPH_DATA:
      if (state.data_idx === action.data_idx) {
        const now = new Date();
        const start_time = action.include_history ? null : new Date(lastMidnightDate(now)).getTime();
        const end_time = action.short_future ? new Date(addDays(lastMidnightDate(now), 2)).getTime() : null;

        const new_graph_map = emptyGraphMap(graphsConfig);
        graphsConfig.forEach((graph) => {
          new_graph_map[graph.id] = _.values(graph_cache[graph.id])
                                     .sort((a, b) => a.sortIndex - b.sortIndex)
                                     .filter(e => (!start_time || e.sortIndex >= start_time) &&
                                                  (!end_time || e.sortIndex < end_time));
        });

        const ticks = extractTicks(new_graph_map, graphsConfig[0].id);

        const filtered_table_data = _.values(table_cache)
                                     .filter(e => (!start_time || e.sortIndex >= start_time) &&
                                                  (!end_time || e.sortIndex < end_time));
        const new_table_map = _.keyBy(filtered_table_data, 'name');

        return {
          ...state,
          ticks,
          graph_data: new_graph_map,
          table_data: new_table_map
        };
      } else {
        return state;
      }

    case ActionTypes.UPDATE_INDICATOR_TIMES:
      const new_table_meta_data = state.table_meta_data.map((md) => {
        if (md.key === action.indicator_prefix) {
          md.creationTime = md.use_import_time_over_creation_time ?
                            action.importTime :
                            action.creationTime;
        }
        return md;
      });

      return {
        ...state,
        table_meta_data: new_table_meta_data
      };

    default:
      return state;
  }
};

export default reducer;



// WEBPACK FOOTER //
// ./src/reducers/point_data_weather.js